import React from 'react';
import { Link } from 'react-router-dom';

import { Tour } from 'generated/mos/org/getty/audio';
import { Media } from 'generated/mos/media'

import * as entity from 'entity';

import { ImageWithFallback } from 'components/ui/image';
import { IconHeadphones, IconMarker, IconCategory, IconWarning } from 'components/ui/icons';
import { space } from 'helpers/style';
import styled from 'styled';
import { mediaQueries } from 'styled/core'

import { tourSchema } from '../../schemas/tour'

const Wrapper = styled.div`
  padding: ${space(3)};

  width: 100%;
  ${mediaQueries.palm} {
    width: calc(100% / 2);
  };
  ${mediaQueries.hand} {
    width: calc(100% / 3);
  };
  ${mediaQueries.lap} {
    width: calc(100% / 5);
  };
`;

const Card = styled(Link)`
  display: block;
  border: 1px solid ${({ theme }) => theme.color.grayL90};
  border-radius: 5px;
  overflow: hidden;
  transition: box-shadow 200ms ease;
  &:hover {
    box-shadow: 0 2px 4px 0 rgba(0,0,0,0.10);
  }
`;

const Title = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.base};
  color: ${({ theme }) => theme.color.darkText};
  height: ${space(10)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

const Body = styled.div`
  flex-shrink: 0;
  height: ${space(25)};
  padding: ${space(3)};
  background-color: ${({ theme }) => theme.color.white};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const Image = styled(ImageWithFallback)`
  height: ${space(40)};
`;

const IconLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${space(4)};
  font-size: ${({ theme }) => theme.font.size.small};
  color: ${({ theme }) => theme.color.grayL40};
`;

const Warning = styled(IconLabelWrapper)`
  margin-left:auto;
  margin-right:0;
`

const IconsContainer = styled.div`
  display: flex;
`

const IconLabel = ({ icon, label }: {icon: React.ReactNode; label: string | number}) => (
  <IconLabelWrapper>
    {icon}<div style={{ marginLeft: space(1, false) }}>{label}</div>
  </IconLabelWrapper>
);


type Props = {
  readonly tour: Tour.Entity;
  readonly projectId: string;
  readonly hasWarning?: boolean;
  readonly hostName: string;
}

export const TourCard = ({ tour, projectId, hasWarning, hostName }: Props) => {
  const stopsCount = tour.stopRefs ? tour.stopRefs.length : 0
  const tourRef = entity.mustRef(tour.ref)

  const tourImage = tourSchema.imageRef.read(tour.imageRef) as Media.Ref
  const tourImageId = tourImage && tourImage.id

  const imagePath = tourImageId ? `${hostName}/images/${tourImageId}.jpg?s=crop&h=320&w=540` : ''

  return (
    <Wrapper>
      <Card to={`/project/${projectId}/tour/${tourRef.id}`}>
        <Image src={imagePath} />
        <Body>
          <Title>{tourSchema.name.read(tour.name)}</Title>
          <IconsContainer>
            <IconLabel icon={<IconHeadphones size="small" color={'grayL40'} />} label={stopsCount} />
            <IconLabel icon={<IconCategory size="small" color={'grayL40'} />} label={0} />
            <IconLabel icon={<IconMarker size="small" color={'grayL40'} />} label={0} />
            {hasWarning &&<Warning><IconWarning size="small" color={'warning'} /></Warning>}
          </IconsContainer>
        </Body>
      </Card>
    </Wrapper>
  );
};
